import { Tooltip } from 'antd';
import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { AppState } from '../../app-state';
import { Loader } from '../../common/components/loader.component';
import { PrimaryButton } from '../../common/components/primary-button.component';
import { selectActiveSourceDocument } from '../../documents/selectors/source-document.selectors';
import { selectActiveAnnotationSchema } from '../../annotations/selectors/annotation-schema.selectors';
import { AnnotationSchema } from '../../annotations/models/annotation-schema.model';
import { runPredictions } from '../actions/predictions.actions';
import { AnnotationTypeSelector } from './annotation-type-selector.component';

const ButtonWrap = styled.span`
  display: flex;
`;

type PredictButtonProps = {
  predictionsRunning: boolean;
  documentLoaded: boolean;
  schema: AnnotationSchema | null;
  disabled?: boolean;
  predict: (annotationTypes?: string[]) => void;
};

const PredictButtonUnconnected = (props: PredictButtonProps) => {
  const {
    predictionsRunning,
    documentLoaded,
    schema,
    disabled,
    predict,
  } = props;

  const schemaId = schema?.annotationSchemaId;
  const [selectedTypes, setSelectedTypes] = useState<string[]>(
    schema?.annotationTypes.map(t => t.key) ?? [],
  );

  const prevSchemaIdRef = React.useRef(schemaId);
  if (prevSchemaIdRef.current !== schemaId) {
    prevSchemaIdRef.current = schemaId;
    setSelectedTypes(schema?.annotationTypes.map(t => t.key) ?? []);
  }

  const isDisabled =
    disabled || predictionsRunning || !documentLoaded || !schema;

  const onPredict = useCallback(() => {
    predict(selectedTypes);
  }, [selectedTypes, predict]);

  const tooltipMessage = predictionsRunning
    ? 'Predictions are running'
    : selectedTypes.length === 0
    ? 'No annotation types selected'
    : undefined;

  const predictButton = (
    <PrimaryButton
      style={{ borderRight: '1px solid white' }}
      onClick={onPredict}
      disabled={isDisabled || selectedTypes.length === 0}
    >
      <>
        {predictionsRunning && <Loader style={{ width: 30 }} />}
        Predict
      </>
    </PrimaryButton>
  );

  const wrappedButton =
    tooltipMessage !== undefined ? (
      <Tooltip placement="left" title={tooltipMessage}>
        {predictButton}
      </Tooltip>
    ) : (
      predictButton
    );

  return (
    <ButtonWrap>
      {wrappedButton}
      <AnnotationTypeSelector
        selectedTypes={selectedTypes}
        onSelectionChange={setSelectedTypes}
        disabled={disabled || predictionsRunning}
      />
    </ButtonWrap>
  );
};

export const PredictionSelectorPopover = connect(
  (state: AppState) => ({
    predictionsRunning: state.predictions.predictionsStatus === 'running',
    documentLoaded: !!selectActiveSourceDocument(state),
    schema: selectActiveAnnotationSchema(state),
  }),
  dispatch => ({
    predict: (annotationTypes?: string[]) => {
      dispatch(runPredictions(annotationTypes));
    },
  }),
)(PredictButtonUnconnected);
